import { Phone, MessageCircle, Mail } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { DropHeading } from "./DropHeading";
import { useEnquiry } from "../hooks/useEnquiry";
import { BRAND, PROPERTIES } from "../properties";

const inputCls =
  "w-full rounded-none border-b border-erth-black/25 bg-transparent px-0 py-3 font-body text-base font-light text-erth-black placeholder:text-erth-black/35 outline-none transition-colors focus:border-erth-black";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1 block font-body text-xs tracking-[0.2em] text-erth-black/50">
      {label}
    </label>
    {children}
  </div>
);

export const ErthContact = () => {
  const { form, set, submit, sending } = useEnquiry("");

  return (
    <section id="contact" data-testid="erth-contact" className="bg-erth-beige py-20 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 sm:px-8 lg:grid-cols-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="mb-6 font-body text-sm tracking-[0.25em] text-erth-black/50">contact us</p>
          <DropHeading testId="erth-contact-heading" text="plan your escape with us" className="text-3xl leading-[1.15] text-erth-black sm:text-4xl lg:text-5xl" />
          <p className="mt-7 max-w-md font-body text-lg font-light leading-relaxed text-erth-black/75">
            Tell us where you'd like to stay and who you're bringing. We'll take it from there.
          </p>
          <div className="mt-10 flex flex-col gap-4 font-body text-base font-light text-erth-black/80">
            <a href={BRAND.phoneHref} data-testid="erth-contact-phone" className="flex items-center gap-3 transition-opacity hover:opacity-60">
              <Phone size={17} strokeWidth={1.5} /> {BRAND.phone}
            </a>
            <a href={BRAND.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="erth-contact-whatsapp" className="flex items-center gap-3 transition-opacity hover:opacity-60">
              <MessageCircle size={17} strokeWidth={1.5} /> {BRAND.whatsapp} (WhatsApp)
            </a>
            <a href={`mailto:${BRAND.email}`} data-testid="erth-contact-email" className="flex items-center gap-3 transition-opacity hover:opacity-60">
              <Mail size={17} strokeWidth={1.5} /> {BRAND.email}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="erth-enquiry-form" className="grid gap-7 sm:grid-cols-2">
            <Field label="name" htmlFor="erth-name">
              <input id="erth-name" data-testid="erth-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </Field>
            <Field label="phone" htmlFor="erth-phone">
              <input id="erth-phone" data-testid="erth-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91" className={inputCls} />
            </Field>
            <Field label="email" htmlFor="erth-email">
              <input id="erth-email" data-testid="erth-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </Field>
            <Field label="property" htmlFor="erth-property">
              <select id="erth-property" data-testid="erth-property-select" value={form.property} onChange={set("property")} className={inputCls}>
                <option value="">General enquiry</option>
                {PROPERTIES.filter((p) => p.live).map((p) => (
                  <option key={p.slug} value={p.name}>{p.name}</option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="message" htmlFor="erth-message">
                <textarea id="erth-message" data-testid="erth-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Dates, group size, or anything we should know" className={`${inputCls} resize-none`} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="erth-submit-button"
                disabled={sending}
                className="rounded-full bg-erth-black px-10 py-4 font-head text-[14px] font-semibold lowercase text-erth-white transition-colors hover:bg-erth-black/80 disabled:opacity-60"
              >
                {sending ? "sending" : "send enquiry"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
